import type { Temporal } from '@js-temporal/polyfill'
import type { IPlaybackScheduleRoot } from '@lucimark/shared/models/player-registration-payload'
import type { ResolvePlaybackSegmentsOptions } from '@lucimark/shared/helpers/playbackScheduling'
import { resolvePlaybackSegments } from '@lucimark/shared/helpers/playbackScheduling'
import {
  restoreEmbeddedSelectionState,
  snapshotEmbeddedSelectionState
} from '@lucimark/shared/helpers/selectEmbeddedFrames'

function assetUrnsFromSegmentFacets(segment: {
  frame: { facets?: Record<string, { asset?: string } | null | undefined> }
}): string[] {
  const urns: string[] = []
  for (const facet of Object.values(segment.frame.facets ?? {})) {
    if (facet != null && typeof facet === 'object' && typeof facet.asset === 'string' && facet.asset !== '') {
      urns.push(facet.asset)
    }
  }
  return urns
}

/**
 * Asset URNs needed to paint the first on-air frame of `root` at `referenceInstant`
 * (ready gate for soft-defer). Preload is not required for ready — it downloads in parallel /
 * with the rest after head is cached so Studio assign can cut over sooner.
 * Does not mutate the active playlist snapshot embed decks.
 */
export function collectPendingPlaylistHeadAssetUrns(
  root: IPlaybackScheduleRoot | null | undefined,
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): string[] {
  if (root == null || (root.playlists?.length ?? 0) === 0) {
    return []
  }
  const snap = snapshotEmbeddedSelectionState()
  try {
    const segments = resolvePlaybackSegments(root, referenceInstant, options)
    if (segments.length === 0) {
      return []
    }
    return [...new Set(assetUrnsFromSegmentFacets(segments[0]!))]
  }
  finally {
    restoreEmbeddedSelectionState(snap)
  }
}

/**
 * Preload-slot asset URNs (segment after head). Kick these off in parallel with head fetch;
 * they must not block {@link collectPendingPlaylistHeadAssetUrns} ready.
 */
export function collectPendingPlaylistPreloadAssetUrns(
  root: IPlaybackScheduleRoot | null | undefined,
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): string[] {
  if (root == null || (root.playlists?.length ?? 0) === 0) {
    return []
  }
  const snap = snapshotEmbeddedSelectionState()
  try {
    const segments = resolvePlaybackSegments(root, referenceInstant, options)
    if (segments.length < 2) {
      return []
    }
    const headSet = new Set(assetUrnsFromSegmentFacets(segments[0]!))
    return [...new Set(assetUrnsFromSegmentFacets(segments[1]!))].filter(urn => !headSet.has(urn))
  }
  finally {
    restoreEmbeddedSelectionState(snap)
  }
}

/** Remaining schedule URNs after the ready-gate head set — fetch these after (or during) rematch. */
export function restPendingPlaylistPrefetchUrns(
  headUrns: readonly string[],
  allUrns: readonly string[]
): string[] {
  const headSet = new Set(headUrns)
  return allUrns.filter(urn => !headSet.has(urn))
}
